/**
 * Expediente digital por contraparte — Materialidad Fases A–D.
 * Aditivo: no modifica Faltantes, Visor, robot SAT ni descargas CFDI.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  ChevronDown,
  Download,
  FileText,
  FileUp,
  FolderOpen,
  Lightbulb,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api from "@/lib/api";
import {
  classifyMaterialidadCounterparty,
  fetchMaterialidadCounterparties,
  fetchMaterialidadExpediente,
  fetchMaterialidadSuggestions,
  materialidadCounterpartyFolderCartaPdfUrl,
  materialidadCounterpartyFolderResumenPdfUrl,
  materialidadCounterpartyFolderZipUrl,
  materialidadCartaClientePdfUrl,
  materialidadCartaClienteXlsxUrl,
  materialidadOperationFolderCartaPdfUrl,
  materialidadOperationFolderResumenPdfUrl,
  materialidadOperationFolderZipUrl,
  openMaterialidadOperationByUuid,
  patchMaterialidadOperation,
  prepareMaterialidadCounterpartyEmail,
  prepareMaterialidadOperationEmail,
  sendMaterialidadCounterpartyEmail,
  sendMaterialidadOperationEmail,
  syncMaterialidadCounterparties,
  syncMaterialidadOperations,
  uploadMaterialidadCounterpartyDocument,
  uploadMaterialidadOperationDocument,
} from "@/modules/tramites/materialidad/materialidadApi";
import MaterialidadPeriodFilter from "@/modules/tramites/materialidad/MaterialidadPeriodFilter";
import { filterActiveCfdiRows, sumCfdiNetTotal, sumPriorYearPayments, isPriorYearPayment, filterCfdiRowsForRiskView, showPriorYearPaymentsForRiskFilter } from "@/modules/tramites/materialidad/materialidadCfdiNet";
import {
  COUNTERPARTY_RISK_FILTER_ALL,
  COUNTERPARTY_RISK_FILTER_ALTO,
  COUNTERPARTY_RISK_FILTER_BAJO,
  COUNTERPARTY_RISK_FILTER_MEDIO,
  RISK_BADGE_CLASS,
  RISK_LABELS,
  buildPeriodQuery,
  counterpartyMatchesRiskFilter,
  defaultPeriodSelection,
  formatPeriodLabel,
  formatPeriodSelectionLabel,
} from "@/modules/tramites/materialidad/materialidadConstants";

const REL_SUPPLIER = "supplier";
const REL_CUSTOMER = "customer";

const RISK_FILTER_OPTIONS = [
  { id: COUNTERPARTY_RISK_FILTER_ALTO, label: "Alto" },
  { id: COUNTERPARTY_RISK_FILTER_MEDIO, label: "Medio" },
  { id: COUNTERPARTY_RISK_FILTER_BAJO, label: "Bajo" },
  { id: COUNTERPARTY_RISK_FILTER_ALL, label: "Todos" },
];

const STATUS_LABELS = {
  detected: "Detectada",
  classified: "Clasificada",
  active: "Activa",
  archived: "Archivada",
};

const OP_STATUS_LABELS = {
  open: "Abierta",
  en_revision: "En revisión",
  completo: "Completa",
  archivado: "Archivada",
};

const INTEGRATION_CLASS = {
  completo: "bg-emerald-50 text-emerald-900 border-emerald-200",
  parcial: "bg-amber-50 text-amber-900 border-amber-200",
  incompleto: "bg-rose-50 text-rose-900 border-rose-200",
  critico: "bg-rose-100 text-rose-950 border-rose-300",
  pendiente_revision: "bg-slate-50 text-slate-600 border-slate-200",
};

const SUGGESTION_PRIORITY_CLASS = {
  crítico: "border-rose-300 bg-rose-50 text-rose-950",
  alto: "border-amber-300 bg-amber-50 text-amber-950",
  medio: "border-slate-200 bg-white text-slate-800",
  bajo: "border-slate-100 bg-slate-50 text-slate-600",
};

const OPERATION_TYPE_OPTIONS = [
  ["mercancia", "Mercancía"],
  ["servicio", "Servicio"],
  ["repse", "REPSE"],
  ["mantenimiento", "Mantenimiento"],
  ["general", "General"],
];

const OPERATION_TYPE_LABELS = Object.fromEntries(OPERATION_TYPE_OPTIONS);

/** Valor del selector cuando no hay proveedor concreto (opción predeterminada). */
const PROVIDER_SELECT_ALL = "__all__";

function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function tipoComprobanteLabel(t) {
  const k = String(t || "").toUpperCase();
  if (k === "I") return "Ingreso (factura)";
  if (k === "E") return "Egreso (NC)";
  if (k === "P") return "Complemento de pago";
  return t || "—";
}

function groupCfdiByMonth(items) {
  const map = new Map();
  for (const row of filterActiveCfdiRows(items)) {
    const key = row.period_key || String(row.fecha || "").slice(0, 7) || "sin-periodo";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  }
  return Array.from(map.entries())
    .sort((a, b) => String(b[0]).localeCompare(String(a[0])))
    .map(([periodKey, rows]) => ({
      periodKey,
      label: periodKey === "sin-periodo" ? "Sin periodo" : formatPeriodLabel(periodKey),
      rows,
      total: sumCfdiNetTotal(rows),
      priorYearPayments: sumPriorYearPayments(rows),
      countI: rows.filter((r) => String(r.tipo_comprobante || "").toUpperCase() === "I").length,
      countP: rows.filter((r) => String(r.tipo_comprobante || "").toUpperCase() === "P").length,
      countE: rows.filter((r) => String(r.tipo_comprobante || "").toUpperCase() === "E").length,
    }));
}

function splitRowsFacturasYPagos(rows) {
  const facturas = [];
  const pagos = [];
  for (const row of rows || []) {
    const t = String(row?.tipo_comprobante || "").toUpperCase();
    if (t === "P") pagos.push(row);
    else if (t === "I" || t === "E") facturas.push(row);
  }
  return { facturas, pagos };
}

export default function MaterialidadContrapartesPanel({
  clientId,
  embeddedInClient = false,
  /** Si viene del panel Faltantes, comparte el mismo periodo (evita filtros desfasados). */
  periodSelection: periodSelectionProp = null,
  onPeriodSelectionChange = null,
}) {
  const [periodSelectionLocal, setPeriodSelectionLocal] = useState(defaultPeriodSelection);
  const periodSelection = periodSelectionProp || periodSelectionLocal;
  const setPeriodSelection = onPeriodSelectionChange || setPeriodSelectionLocal;
  const [relFilter, setRelFilter] = useState(REL_SUPPLIER);
  /** Default Alto: priorizar revisión sin mostrar todo el catálogo al abrir. */
  const [riskFilter, setRiskFilter] = useState(COUNTERPARTY_RISK_FILTER_ALTO);
  const [searchQ, setSearchQ] = useState("");
  /** Búsqueda local del selector de proveedor (nombre / RFC). */
  const [providerPickerSearch, setProviderPickerSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [list, setList] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [expediente, setExpediente] = useState(null);
  const [docType, setDocType] = useState("constancia_fiscal");
  const [operationDetail, setOperationDetail] = useState(null);
  const [opDocType, setOpDocType] = useState("evidencia_entrega");
  const [syncingOps, setSyncingOps] = useState(false);
  const [uploadingOp, setUploadingOp] = useState(false);
  const [openingOp, setOpeningOp] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [detailTab, setDetailTab] = useState("cfdi_pagos");
  /** Acordeón de sugerencias: cerrado por defecto; se cierra al cambiar de proveedor. */
  const [suggestionsAccordion, setSuggestionsAccordion] = useState("");
  /** Panel para reclasificar tipo de operación (ficha ya clasificada). */
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  /** Meses del detalle CFDI: cerrado por defecto (`true` = expandido). */
  const [openMonths, setOpenMonths] = useState({});
  const [lastSyncMeta, setLastSyncMeta] = useState(null);
  /** Evita re-cargar la misma clave cliente|periodo|relación (solo lectura, sin sync CFDI). */
  const listLoadKeyRef = useRef("");
  const selectedIdRef = useRef("");

  const periodQuery = useMemo(() => buildPeriodQuery(periodSelection), [periodSelection]);
  const periodLabel = list?.period_label || formatPeriodSelectionLabel(periodSelection);
  const periodKeySig = useMemo(
    () => JSON.stringify(periodQuery),
    [periodQuery],
  );
  const cfdiRowsForView = useMemo(
    () => filterCfdiRowsForRiskView(expediente?.items || [], riskFilter),
    [expediente?.items, riskFilter],
  );
  const { facturas: cfdiFacturasRows, pagos: cfdiPagosRows } = useMemo(
    () => splitRowsFacturasYPagos(cfdiRowsForView),
    [cfdiRowsForView],
  );
  const cfdiByMonthFacturas = useMemo(
    () => groupCfdiByMonth(cfdiFacturasRows),
    [cfdiFacturasRows],
  );
  const cfdiByMonthPagos = useMemo(
    () => groupCfdiByMonth(cfdiPagosRows),
    [cfdiPagosRows],
  );
  const showPriorYearBlock = showPriorYearPaymentsForRiskFilter(riskFilter);
  const items = useMemo(() => list?.items || [], [list?.items]);
  const riskCounts = list?.risk_counts || null;
  const filteredItems = useMemo(() => {
    const matched = items.filter((row) => counterpartyMatchesRiskFilter(row, riskFilter));
    // Si el filtro deja vacío (p. ej. sin altos), mostrar todos para no romper el selector.
    if (!matched.length && items.length && riskFilter !== COUNTERPARTY_RISK_FILTER_ALL) {
      return items;
    }
    return matched;
  }, [items, riskFilter]);

  const providerSearchNeedle = providerPickerSearch.trim().toLowerCase();

  const providersForSelect = useMemo(() => {
    let rows = filteredItems;
    if (providerSearchNeedle) {
      rows = filteredItems.filter((row) => {
        const name = String(row.legal_name || "").toLowerCase();
        const rfc = String(row.rfc || "").toLowerCase();
        return name.includes(providerSearchNeedle) || rfc.includes(providerSearchNeedle);
      });
    }
    if (selectedId && !rows.some((r) => r.id === selectedId)) {
      const selected = filteredItems.find((r) => r.id === selectedId)
        || items.find((r) => r.id === selectedId);
      if (selected) rows = [selected, ...rows];
    }
    return rows;
  }, [filteredItems, items, providerSearchNeedle, selectedId]);

  const riskFilterEmptyFallback = Boolean(
    items.length
    && riskFilter !== COUNTERPARTY_RISK_FILTER_ALL
    && !items.some((row) => counterpartyMatchesRiskFilter(row, riskFilter)),
  );
  const docTypes = expediente?.document_types || list?.document_types || [];
  const cp = useMemo(() => {
    if (!selectedId) return null;
    return items.find((r) => r.id === selectedId)
      || filteredItems.find((r) => r.id === selectedId)
      || expediente?.counterparty
      || null;
  }, [selectedId, items, filteredItems, expediente?.counterparty]);
  /** Valor del selector: «Todos» por defecto; id del proveedor si está en la lista. */
  const selectValue = useMemo(() => {
    if (selectedId && filteredItems.some((r) => r.id === selectedId)) return selectedId;
    return PROVIDER_SELECT_ALL;
  }, [selectedId, filteredItems]);

  const selectProvider = useCallback((id) => {
    selectedIdRef.current = id || "";
    setSelectedId(id || "");
    setOperationDetail(null);
    setDetailTab("cfdi_pagos");
    setSuggestionsAccordion("");
    setOpenMonths({});
    setReclassifyOpen(false);
  }, []);

  // Si la lista o el filtro de riesgo cambian y la selección ya no aplica, volver a «Todos».
  useEffect(() => {
    if (!filteredItems.length) {
      if (selectedIdRef.current) {
        selectedIdRef.current = "";
        setSelectedId("");
        setExpediente(null);
      }
      return;
    }
    if (selectedIdRef.current && !filteredItems.some((r) => r.id === selectedIdRef.current)) {
      selectedIdRef.current = "";
      setSelectedId("");
      setExpediente(null);
      setOperationDetail(null);
      setDetailTab("cfdi_pagos");
    }
  }, [filteredItems, items.length]);

  const loadList = useCallback(async ({ preferSelectFirst = false, onlyWithPeriodCfdi = true } = {}) => {
    if (!clientId) {
      setList(null);
      return null;
    }
    setLoading(true);
    try {
      const data = await fetchMaterialidadCounterparties({
        clientId,
        ...periodQuery,
        relationshipType: relFilter,
        q: searchQ.trim() || undefined,
        onlyWithPeriodCfdi,
      });
      setList(data);
      const rows = data?.items || [];
      const stillSelected = selectedIdRef.current
        && rows.some((r) => r.id === selectedIdRef.current);
      if (!stillSelected) {
        if (preferSelectFirst && rows[0]?.id) {
          selectProvider(rows[0].id);
        } else {
          selectProvider("");
          setExpediente(null);
        }
      }
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar el expediente de contrapartes.");
      setList(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [clientId, periodQuery, relFilter, searchQ, selectProvider]);

  const loadSuggestions = useCallback(async (id) => {
    if (!id) {
      setSuggestions(null);
      return;
    }
    setSuggestionsLoading(true);
    try {
      const data = await fetchMaterialidadSuggestions(id, periodQuery);
      setSuggestions(data);
    } catch {
      setSuggestions(null);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [periodQuery]);

  const loadExpediente = useCallback(async (id) => {
    if (!id) {
      setExpediente(null);
      setSuggestions(null);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchMaterialidadExpediente(id, periodQuery);
      setExpediente(data);
      await loadSuggestions(id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo abrir el expediente.");
      setExpediente(null);
      setSuggestions(null);
    } finally {
      setLoading(false);
    }
  }, [periodQuery, loadSuggestions]);

  /** Detecta proveedores desde CFDI del Visor (deja «Todos» predeterminado en el selector). */
  const bootstrapFromCfdi = useCallback(async ({ quiet = false } = {}) => {
    if (!clientId) return;
    setSyncing(true);
    try {
      const result = await syncMaterialidadCounterparties(clientId, periodQuery);
      setLastSyncMeta(result || null);
      const data = await loadList({ preferSelectFirst: false, onlyWithPeriodCfdi: true });
      const n = (data?.items || []).length;
      if (!quiet) {
        toast.success(
          n > 0
            ? `${n} proveedor(es)/contraparte(s) en el selector · `
              + `${result.created || 0} nueva(s), ${result.updated || 0} actualizada(s) `
              + `(${result.scanned_cfdi || 0} CFDI revisados).`
            : `Sin contrapartes con CFDI en el periodo `
              + `(${result.scanned_cfdi || 0} CFDI revisados en Visor). `
              + "Pruebe otro ejercicio o «Clientes e ingresos».",
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo sincronizar desde CFDI.");
    } finally {
      setSyncing(false);
    }
  }, [clientId, periodQuery, loadList]);

  // Al abrir (o cambiar cliente/periodo/relación): solo leer contrapartes ya guardadas.
  // El sync desde CFDI es manual («Actualizar desde CFDI») para evitar doble carga.
  useEffect(() => {
    if (!clientId) return undefined;
    const key = `${clientId}|${periodKeySig}|${relFilter}`;
    if (listLoadKeyRef.current === key) return undefined;
    listLoadKeyRef.current = key;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadList({ preferSelectFirst: false, onlyWithPeriodCfdi: true });
    })();
    return () => { cancelled = true; };
  }, [clientId, periodKeySig, relFilter, loadList]);

  // Búsqueda: solo refiltra lista (sin re-sync).
  const searchQRef = useRef(searchQ);
  useEffect(() => {
    if (searchQRef.current === searchQ) return undefined;
    searchQRef.current = searchQ;
    if (!clientId || !listLoadKeyRef.current) return undefined;
    const t = setTimeout(() => {
      loadList({ preferSelectFirst: false, onlyWithPeriodCfdi: true });
    }, 280);
    return () => clearTimeout(t);
  }, [searchQ, clientId, loadList]);

  useEffect(() => {
    if (selectedId) loadExpediente(selectedId);
  }, [selectedId, loadExpediente]);

  const handleSync = async () => {
    await bootstrapFromCfdi({ quiet: false });
    if (clientId) {
      listLoadKeyRef.current = `${clientId}|${periodKeySig}|${relFilter}`;
    }
  };

  const handleClassify = async (operationType) => {
    if (!selectedId) return;
    const wasClassified = String(cp?.status || "") !== "detected";
    try {
      await classifyMaterialidadCounterparty(selectedId, {
        classified_operation_type: operationType,
        status: "classified",
      });
      toast.success(
        wasClassified
          ? "Tipo de operación reasignado."
          : "Contraparte clasificada.",
      );
      setReclassifyOpen(false);
      await loadList();
      await loadExpediente(selectedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo clasificar.");
    }
  };

  const handleUpload = async (file) => {
    if (!selectedId || !file) return;
    setUploading(true);
    try {
      await uploadMaterialidadCounterpartyDocument(selectedId, file, {
        documentType: docType,
      });
      toast.success("Documento cargado (versionado).");
      await loadExpediente(selectedId);
      await loadList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir el documento.");
    } finally {
      setUploading(false);
    }
  };

  const handleForceAudit = async () => {
    if (!clientId) return;
    try {
      await api.post("/internal/audit", {
        module_name: "tramites_materialidad_counterparties",
        client_id: clientId,
        trigger_healing: false,
      });
      await api.post("/internal/audit", {
        module_name: "tramites_materialidad_operations",
        client_id: clientId,
        trigger_healing: false,
      });
      toast.success("Auditoría de fichas y operaciones solicitada.");
      await loadList();
      if (selectedId) await loadExpediente(selectedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo forzar la auditoría.");
    }
  };

  const handleSyncOperations = async () => {
    if (!selectedId) return;
    setSyncingOps(true);
    try {
      const result = await syncMaterialidadOperations(selectedId, periodQuery);
      toast.success(
        `Operaciones MAT-*: ${result.created || 0} nueva(s), ${result.updated || 0} actualizada(s).`,
      );
      await loadExpediente(selectedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron crear operaciones desde CFDI.");
    } finally {
      setSyncingOps(false);
    }
  };

  const handleOpenOperation = async (cfdiUuid) => {
    if (!selectedId || !cfdiUuid) return;
    setOpeningOp(true);
    try {
      const detail = await openMaterialidadOperationByUuid(selectedId, cfdiUuid, periodQuery);
      setOperationDetail(detail);
      const types = detail.document_types || [];
      if (types.length && !types.some((t) => t.id === opDocType)) {
        setOpDocType(types[0].id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo abrir la operación.");
    } finally {
      setOpeningOp(false);
    }
  };

  const handlePatchOperation = async (payload) => {
    const caseId = operationDetail?.operation?.id;
    if (!caseId) return;
    try {
      await patchMaterialidadOperation(caseId, payload);
      toast.success("Operación actualizada.");
      const detail = await openMaterialidadOperationByUuid(
        selectedId,
        operationDetail.operation.cfdi_uuid,
        periodQuery,
      );
      setOperationDetail(detail);
      await loadExpediente(selectedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar la operación.");
    }
  };

  const handleUploadOperationDoc = async (file) => {
    const caseId = operationDetail?.operation?.id;
    if (!caseId || !file) return;
    setUploadingOp(true);
    try {
      await uploadMaterialidadOperationDocument(caseId, file, { documentType: opDocType });
      toast.success("Documento de operación cargado (versionado).");
      const detail = await openMaterialidadOperationByUuid(
        selectedId,
        operationDetail.operation.cfdi_uuid,
        periodQuery,
      );
      setOperationDetail(detail);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir el documento de operación.");
    } finally {
      setUploadingOp(false);
    }
  };

  const handlePrepareEmail = async (scope) => {
    setEmailBusy(true);
    try {
      const draft = scope === "operation" && operationDetail?.operation?.id
        ? await prepareMaterialidadOperationEmail(operationDetail.operation.id)
        : await prepareMaterialidadCounterpartyEmail(selectedId, periodQuery);
      if (draft.mailto_url) {
        window.open(draft.mailto_url, "_blank", "noopener,noreferrer");
        toast.success("Borrador abierto en su cliente de correo. Adjunte el ZIP descargado si aplica.");
      } else {
        toast.message(draft.subject || "Borrador listo", {
          description: draft.attachment_hint || "Descargue el ZIP y envíelo manualmente.",
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo preparar el correo.");
    } finally {
      setEmailBusy(false);
    }
  };

  const handleSendEmail = async (scope) => {
    setEmailBusy(true);
    try {
      const result = scope === "operation" && operationDetail?.operation?.id
        ? await sendMaterialidadOperationEmail(operationDetail.operation.id, { attachZip: true })
        : await sendMaterialidadCounterpartyEmail(selectedId, {
          attachZip: true,
          periodQuery,
        });
      if (result.ok) {
        toast.success("Correo enviado con ZIP del expediente (si el transporte está listo).");
      } else {
        toast.error(result.result?.detail || "No se pudo enviar el correo.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar el correo.");
    } finally {
      setEmailBusy(false);
    }
  };

  if (!clientId) {
    return (
      <div
        className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        data-testid="materialidad-contrapartes-need-client"
      >
        {embeddedInClient
          ? "Abra un cliente guardado para ver Materialidad."
          : "Seleccione un cliente de trabajo en la barra superior para abrir Materialidad."}
        {" "}Esto no modifica el Visor ni las descargas SAT.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="materialidad-contrapartes-panel">
      <div className="rounded-md border border-slate-200 bg-white px-3 py-2.5 shadow-sm space-y-2">
        {!periodSelectionProp ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <MaterialidadPeriodFilter
              idPrefix="materialidad-contrapartes"
              value={periodSelection}
              onChange={setPeriodSelection}
              compact
            />
          </div>
        ) : null}
        <div className={`flex flex-wrap items-center gap-2 ${!periodSelectionProp ? "border-t border-slate-100 pt-2" : ""}`}>
          <p className="flex-1 min-w-[140px] text-xs text-slate-600 m-0">
            {embeddedInClient
              ? `Proveedores del periodo ${periodLabel} · selector por RFC detectado`
              : `Expediente permanente por RFC · periodo ${periodLabel} (filtro)`}
            <span className="block text-[10px] text-slate-500">
              Agrupa CFDI del Visor por proveedor; no descarga del SAT ni duplica XML.
              Al abrir solo lee fichas ya guardadas; use «Actualizar desde CFDI» si subió XML nuevos.
              Elija el proveedor y use las subpestañas CFDI/complementos o Documentos.
            </span>
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm h-8"
            disabled={syncing || loading}
            onClick={handleSync}
            data-testid="materialidad-counterparties-sync"
          >
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Actualizar desde CFDI
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm h-8"
            disabled={loading || syncing}
            onClick={() => loadList({ preferSelectFirst: !selectedId, onlyWithPeriodCfdi: true })}
          >
            Actualizar lista
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="rounded-sm h-8 text-xs text-slate-600"
            disabled={loading || syncing}
            onClick={handleForceAudit}
            data-testid="materialidad-counterparties-force-audit"
          >
            Forzar auditoría
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={relFilter === REL_SUPPLIER ? "default" : "outline"}
          className="rounded-sm h-8"
          onClick={() => setRelFilter(REL_SUPPLIER)}
          data-testid="materialidad-rel-supplier"
        >
          Proveedores y gastos
        </Button>
        <Button
          type="button"
          size="sm"
          variant={relFilter === REL_CUSTOMER ? "default" : "outline"}
          className="rounded-sm h-8"
          onClick={() => setRelFilter(REL_CUSTOMER)}
          data-testid="materialidad-rel-customer"
        >
          Clientes e ingresos
        </Button>
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Buscar RFC o nombre…"
            className="pl-8 h-8 text-sm"
            data-testid="materialidad-counterparties-search"
          />
        </div>
      </div>

      <div className="space-y-3 min-h-[420px]">
        <div
          className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden"
          data-testid="materialidad-counterparties-list"
        >
          <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#0F2D5C]" />
            <h2 className="text-sm font-bold text-[#0F2D5C] m-0">
              {relFilter === REL_SUPPLIER ? "Proveedores (candidatos)" : "Clientes (ingresos)"}
            </h2>
            <span className="text-[10px] text-slate-500 ml-auto">
              {syncing || loading
                ? "Detectando…"
                : riskFilter === COUNTERPARTY_RISK_FILTER_ALL
                  ? `${items.length} detectado(s)`
                  : `${filteredItems.length} de ${items.length} · filtro ${
                    RISK_FILTER_OPTIONS.find((o) => o.id === riskFilter)?.label || riskFilter
                  }`}
            </span>
          </div>
          {syncing && !items.length ? (
            <div className="px-3 py-6 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Detectando proveedores desde CFDI del Visor…
            </div>
          ) : items.length === 0 ? (
            <div className="px-3 py-6 text-sm text-slate-500 space-y-2">
              <p className="m-0 font-medium text-slate-700">
                Sin {relFilter === REL_SUPPLIER ? "proveedores" : "clientes"} detectados en el periodo {periodLabel}.
              </p>
              {lastSyncMeta ? (
                <p className="m-0 text-[11px] text-slate-500">
                  Última detección: {Number(lastSyncMeta.scanned_cfdi || 0)} CFDI de esta empresa en Visor
                  {" · "}
                  {Number(lastSyncMeta.counterparties_touched || 0)} contraparte(s) creada(s)
                  {Number(lastSyncMeta.scanned_cfdi || 0) === 0
                    ? ". Cargue o sincronice XML de esta empresa en el Visor (Mis CFDI / Carga)."
                    : ""}
                  {relFilter === REL_SUPPLIER && Number(lastSyncMeta.customers_in_period || 0) > 0
                    ? ` · hay ${lastSyncMeta.customers_in_period} en «Clientes e ingresos»`
                    : ""}
                  {relFilter === REL_CUSTOMER && Number(lastSyncMeta.suppliers_in_period || 0) > 0
                    ? ` · hay ${lastSyncMeta.suppliers_in_period} en «Proveedores y gastos»`
                    : ""}
                </p>
              ) : (
                <p className="m-0 text-[11px] text-slate-500">
                  Pulse «Volver a detectar desde CFDI». Luego elija el proveedor en el selector
                  para ver CFDI/complementos y Documentos.
                </p>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm h-8"
                disabled={syncing}
                onClick={handleSync}
                data-testid="materialidad-redetect-from-cfdi"
              >
                {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Volver a detectar desde CFDI
              </Button>
            </div>
          ) : (
            <div className="px-3 py-3 space-y-2 bg-slate-50/40" data-testid="materialidad-provider-tabs">
              <div className="flex flex-wrap items-center gap-1.5" data-testid="materialidad-risk-filter">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mr-0.5">
                  Prioridad
                </span>
                {RISK_FILTER_OPTIONS.map((opt) => {
                  const active = riskFilter === opt.id;
                  let count = null;
                  if (riskCounts) {
                    if (opt.id === COUNTERPARTY_RISK_FILTER_ALL) {
                      count = items.length;
                    } else if (opt.id === COUNTERPARTY_RISK_FILTER_ALTO) {
                      count = Number(riskCounts.alto || 0) + Number(riskCounts.critico || 0);
                    } else if (opt.id === COUNTERPARTY_RISK_FILTER_MEDIO) {
                      count = Number(riskCounts.medio || 0);
                    } else if (opt.id === COUNTERPARTY_RISK_FILTER_BAJO) {
                      count = Number(riskCounts.bajo || 0) + Number(riskCounts.none || 0);
                    }
                  }
                  return (
                    <Button
                      key={opt.id}
                      type="button"
                      size="sm"
                      variant={active ? "default" : "outline"}
                      className="rounded-sm h-7 text-xs px-2.5"
                      onClick={() => setRiskFilter(opt.id)}
                      data-testid={`materialidad-risk-filter-${opt.id}`}
                    >
                      {opt.label}
                      {count != null ? (
                        <span className="ml-1 opacity-80">({count})</span>
                      ) : null}
                    </Button>
                  );
                })}
              </div>
              <p className="m-0 text-[10px] text-slate-500 leading-snug">
                Alto: I − E vigentes del mes por emisión (sin P ni cancelados) ≥ umbral de la empresa
                (relativo a ingresos o piso/fijo en Configuración). Bancos e instituciones financieras
                no entran a Alto solo por importe. Crítico: EFOS o cancelado.
                Medio: docs clave faltantes (CSF / 69-B / opinión) u otras señales suaves.
                La carta resumen incluye las contrapartes del filtro activo (mismo listado).
              </p>
              {clientId ? (
                <div
                  className="flex flex-wrap items-center gap-2 rounded-sm border border-sky-100 bg-sky-50/60 px-2.5 py-2"
                  data-testid="materialidad-carta-resumen-cliente"
                >
                  <p className="m-0 text-[11px] text-slate-600 flex-1 min-w-[180px]">
                    Carta resumen al cliente con contrapartes del filtro{" "}
                    <span className="font-semibold text-[#0F2D5C]">
                      {RISK_FILTER_OPTIONS.find((o) => o.id === riskFilter)?.label || "Todos"}
                    </span>
                    {" "}(mayor→menor monto + tipo de riesgo; proveedores y clientes).
                  </p>
                  <Button
                    asChild
                    type="button"
                    size="sm"
                    className="rounded-sm h-8 text-xs shrink-0"
                    data-testid="materialidad-download-carta-resumen"
                  >
                    <a
                      href={materialidadCartaClientePdfUrl({
                        ...periodQuery,
                        clientId,
                        riskFilter,
                        relationshipType: relFilter,
                      })}
                      download
                    >
                      <FileText className="h-3.5 w-3.5 mr-1" />
                      Carta PDF
                    </a>
                  </Button>
                  <Button
                    asChild
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-sm h-8 text-xs shrink-0"
                    data-testid="materialidad-download-carta-excel"
                  >
                    <a
                      href={materialidadCartaClienteXlsxUrl({
                        ...periodQuery,
                        clientId,
                        riskFilter,
                        relationshipType: relFilter,
                      })}
                      download
                    >
                      Excel
                    </a>
                  </Button>
                </div>
              ) : null}
              {riskFilterEmptyFallback ? (
                <p className="m-0 text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-sm px-2 py-1.5">
                  No hay proveedores en «{RISK_FILTER_OPTIONS.find((o) => o.id === riskFilter)?.label}».
                  Mostrando todos para no dejar el selector vacío.
                </p>
              ) : null}
              <div className="space-y-2" data-testid="materialidad-provider-picker">
                <label className="flex flex-col gap-1.5 text-[11px] font-semibold text-slate-600">
                  {relFilter === REL_SUPPLIER ? "Seleccionar proveedor" : "Seleccionar cliente"}
                  <div className="relative">
                    <Search
                      className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 pointer-events-none"
                      aria-hidden
                    />
                    <Input
                      value={providerPickerSearch}
                      onChange={(e) => setProviderPickerSearch(e.target.value)}
                      placeholder="Escriba el nombre o RFC para buscar…"
                      className="h-9 rounded-sm pl-8 text-sm font-normal"
                      data-testid="materialidad-provider-search"
                    />
                  </div>
                </label>
                <Select
                  value={selectValue}
                  onValueChange={(v) => selectProvider(v === PROVIDER_SELECT_ALL ? "" : v)}
                >
                  <SelectTrigger
                    className="h-10 w-full rounded-sm border border-slate-200 bg-white px-2 text-sm font-normal text-slate-900"
                    data-testid="materialidad-provider-select"
                  >
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PROVIDER_SELECT_ALL} data-testid="materialidad-provider-option-all">
                      Todos
                    </SelectItem>
                    {providersForSelect.map((row) => {
                      const name = row.legal_name || row.rfc || "Sin nombre";
                      const tip = [
                        name,
                        row.rfc,
                        RISK_LABELS[row.risk_level] || row.risk_level,
                        `${Number(row.period_cfdi_count || row.cfdi_count_total || 0)} CFDI`,
                        money(row.period_cfdi_total_mxn || row.cfdi_total_mxn),
                      ].filter(Boolean).join(" · ");
                      return (
                        <SelectItem
                          key={row.id}
                          value={row.id}
                          data-testid={`materialidad-cp-option-${row.id}`}
                          title={tip}
                        >
                          {name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {providerSearchNeedle && providersForSelect.length === 0 ? (
                  <p className="m-0 text-[10px] text-amber-800">
                    Ningún {relFilter === REL_SUPPLIER ? "proveedor" : "cliente"} coincide con la búsqueda.
                  </p>
                ) : null}
              </div>
              {cp ? (
                <p className="m-0 text-[11px] text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span>
                    Activo: <span className="font-semibold text-slate-700">{cp.legal_name || cp.rfc}</span>
                    {" · "}
                    <span className="font-mono">{cp.rfc}</span>
                    {" · "}
                    {Number(cp.period_cfdi_count || cp.cfdi_count_total || 0)} CFDI en periodo
                  </span>
                  {cp.risk_level && cp.risk_level !== "none" ? (
                    <span
                      className={`inline-flex rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold ${
                        RISK_BADGE_CLASS[cp.risk_level] || RISK_BADGE_CLASS.bajo
                      }`}
                    >
                      {RISK_LABELS[cp.risk_level] || cp.risk_level}
                    </span>
                  ) : null}
                </p>
              ) : (
                <p className="m-0 text-[11px] text-slate-500" data-testid="materialidad-provider-all-hint">
                  Activo: <span className="font-semibold text-slate-700">Todos</span>
                  {" · "}
                  {filteredItems.length} {relFilter === REL_SUPPLIER ? "proveedor(es)" : "cliente(s)"}
                  {" · "}
                  escriba el nombre arriba o elija uno en la lista
                </p>
              )}
            </div>
          )}
        </div>

        <section
          className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden"
          data-testid="materialidad-expediente-detail"
        >
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500">
              <FolderOpen className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p className="m-0">
                Aquí aparecerán las subpestañas <strong>CFDI y complementos de pago</strong> y <strong>Documentos</strong>
                {" "}cuando elija un proveedor detectado arriba.
              </p>
            </div>
          ) : !selectedId || !cp ? (
            <div className="px-4 py-12 text-center text-sm text-slate-500">
              <FolderOpen className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              {syncing || loading
                ? "Cargando CFDI del proveedor…"
                : "Selección «Todos»: escriba el nombre del proveedor o elíjalo en la lista para ver CFDI/complementos y documentos."}
            </div>
          ) : !expediente || expediente?.counterparty?.id !== selectedId ? (
            <div className="px-4 py-12 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              <p className="m-0">
                Cargando expediente de <strong>{cp.legal_name || cp.rfc}</strong>…
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              <div className="px-4 py-3 flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-bold text-[#0F2D5C] m-0 truncate">{cp.legal_name}</h3>
                  <p className="text-xs font-mono text-slate-600 m-0 mt-0.5">{cp.rfc}</p>
                  <p className="text-[11px] text-slate-500 m-0 mt-1">
                    {STATUS_LABELS[cp.status] || cp.status}
                    {cp.classified_operation_type
                      ? ` · ${OPERATION_TYPE_LABELS[cp.classified_operation_type] || cp.classified_operation_type}`
                      : ""}
                    {" · "}
                    {Number(expediente?.cfdi_count || 0)} CFDI en periodo
                  </p>
                </div>
                <span
                  className={`inline-flex rounded-sm border px-2 py-1 text-[10px] font-semibold ${
                    INTEGRATION_CLASS[expediente?.integration_level] || INTEGRATION_CLASS.pendiente_revision
                  }`}
                >
                  {expediente?.integration_label || "—"}
                </span>
              </div>

              <div
                className="px-4 py-2.5 flex flex-wrap gap-1.5 bg-slate-50/50"
                data-testid="materialidad-folder-export-actions"
              >
                <Button asChild type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs">
                  <a
                    href={materialidadCounterpartyFolderZipUrl(selectedId, periodQuery)}
                    download
                    data-testid="materialidad-cp-download-zip"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    ZIP expediente
                  </a>
                </Button>
                <Button asChild type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs">
                  <a href={materialidadCounterpartyFolderResumenPdfUrl(selectedId, periodQuery)} download>
                    PDF resumen interno
                  </a>
                </Button>
                <Button asChild type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs">
                  <a href={materialidadCounterpartyFolderCartaPdfUrl(selectedId, periodQuery)} download>
                    Carta al proveedor (plantilla)
                  </a>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="rounded-sm h-7 text-xs"
                  disabled={emailBusy}
                  onClick={() => handlePrepareEmail("counterparty")}
                >
                  <Mail className="h-3.5 w-3.5 mr-1" />
                  Abrir correo
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="rounded-sm h-7 text-xs"
                  disabled={emailBusy}
                  onClick={() => handleSendEmail("counterparty")}
                  data-testid="materialidad-cp-send-email"
                >
                  {emailBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                  Enviar con ZIP
                </Button>
              </div>

              <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="rounded-sm border border-slate-100 bg-slate-50/80 px-2 py-1.5">
                  <span className="block text-[10px] text-slate-500">CFDI periodo</span>
                  <span className="font-semibold text-[#0F2D5C]">{expediente?.cfdi_count ?? 0}</span>
                </div>
                <div className="rounded-sm border border-slate-100 bg-slate-50/80 px-2 py-1.5">
                  <span className="block text-[10px] text-slate-500">Facturado periodo (I − E)</span>
                  <span className="font-semibold text-[#0F2D5C]">{money(expediente?.cfdi_total_mxn)}</span>
                  <span className="block text-[10px] text-slate-500 mt-0.5">Sin P ni cancelados</span>
                </div>
                <div className="rounded-sm border border-slate-100 bg-slate-50/80 px-2 py-1.5">
                  <span className="block text-[10px] text-slate-500">Documentos</span>
                  <span className="font-semibold text-[#0F2D5C]">{(expediente?.documents || []).length}</span>
                </div>
                <div className="rounded-sm border border-slate-100 bg-slate-50/80 px-2 py-1.5">
                  <span className="block text-[10px] text-slate-500">Sugerencias</span>
                  <span className="font-semibold text-[#0F2D5C]">
                    {suggestionsLoading ? "…" : (suggestions?.total ?? "—")}
                  </span>
                </div>
              </div>

              {(suggestions?.items || []).length > 0 ? (
                <div
                  className="px-4 py-2"
                  data-testid="materialidad-suggestions-panel"
                >
                  <Accordion
                    type="single"
                    collapsible
                    value={suggestionsAccordion}
                    onValueChange={setSuggestionsAccordion}
                    className="rounded-sm border border-slate-100 bg-slate-50/40"
                    data-testid="materialidad-suggestions-accordion"
                  >
                    <AccordionItem value="sugerencias" className="border-0">
                      <div className="flex items-center gap-1 pr-2">
                        <AccordionTrigger
                          className="flex-1 px-3 py-2.5 text-xs hover:no-underline [&[data-state=open]]:pb-2"
                          data-testid="materialidad-suggestions-trigger"
                        >
                          <span className="flex items-center gap-2 text-left min-w-0">
                            <Lightbulb className="h-4 w-4 text-amber-600 shrink-0" aria-hidden />
                            <span className="font-bold uppercase tracking-wide text-slate-500">
                              Sugerencias priorizadas
                            </span>
                            <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-sm border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-[#0F2D5C]">
                              {suggestions.total ?? suggestions.items.length}
                            </span>
                          </span>
                        </AccordionTrigger>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs shrink-0"
                          disabled={suggestionsLoading}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            loadSuggestions(selectedId);
                          }}
                          data-testid="materialidad-suggestions-recalc"
                        >
                          Recalcular
                        </Button>
                      </div>
                      <AccordionContent className="px-3 pb-3 pt-0 space-y-2">
                        <ul className="space-y-1.5 max-h-56 overflow-y-auto">
                          {(suggestions.items || []).slice(0, 12).map((s) => (
                            <li
                              key={`${s.code}-${s.case_id || ""}-${s.document_type || ""}`}
                              className={`rounded-sm border px-2.5 py-2 text-xs ${
                                SUGGESTION_PRIORITY_CLASS[s.priority_label] || SUGGESTION_PRIORITY_CLASS.medio
                              }`}
                            >
                              <div className="flex flex-wrap items-start gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-wide shrink-0">
                                  {s.priority_label}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold m-0">{s.title}</p>
                                  <p className="text-[11px] opacity-80 m-0 mt-0.5">{s.why}</p>
                                  <p className="text-[11px] m-0 mt-1">{s.suggested_action}</p>
                                </div>
                                <div className="flex flex-wrap gap-1 shrink-0">
                                  {s.document_type && s.scope !== "operation" ? (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-6 px-2 text-[10px] bg-white"
                                      onClick={() => setDocType(s.document_type)}
                                    >
                                      Subir doc
                                    </Button>
                                  ) : null}
                                  {s.cfdi_uuid ? (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-6 px-2 text-[10px] bg-white"
                                      disabled={openingOp}
                                      onClick={() => handleOpenOperation(s.cfdi_uuid)}
                                    >
                                      {s.case_code || "Abrir MAT-*"}
                                    </Button>
                                  ) : null}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                        <p className="text-[10px] text-slate-500 m-0">{suggestions.disclaimer}</p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              ) : null}

              {cp.status === "detected" ? (
                <div className="px-4 py-3 bg-amber-50/60" data-testid="materialidad-classify-panel">
                  <p className="text-xs text-amber-900 m-0 mb-2">
                    Ficha preliminar. Clasifique el tipo de operación para activar plantilla documental.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {OPERATION_TYPE_OPTIONS.map(([id, label]) => (
                      <Button
                        key={id}
                        type="button"
                        size="sm"
                        variant="outline"
                        className="rounded-sm h-7 text-xs"
                        onClick={() => handleClassify(id)}
                        data-testid={`materialidad-classify-${id}`}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="px-4 py-3 bg-slate-50/80" data-testid="materialidad-reclassify-panel">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs text-slate-700 m-0 flex-1 min-w-[12rem]">
                      Tipo de operación:{" "}
                      <strong className="text-[#0F2D5C]">
                        {OPERATION_TYPE_LABELS[cp.classified_operation_type]
                          || cp.classified_operation_type
                          || "Sin clasificar"}
                      </strong>
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant={reclassifyOpen ? "ghost" : "outline"}
                      className="rounded-sm h-7 text-xs"
                      onClick={() => setReclassifyOpen((v) => !v)}
                      data-testid="materialidad-reclassify-toggle"
                    >
                      {reclassifyOpen ? "Cancelar" : "Reasignar tipo"}
                    </Button>
                  </div>
                  {reclassifyOpen ? (
                    <div className="mt-2 space-y-1.5">
                      <p className="text-[11px] text-slate-500 m-0">
                        Elija el nuevo tipo. La plantilla documental se ajustará al cambiar.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {OPERATION_TYPE_OPTIONS.map(([id, label]) => {
                          const active = cp.classified_operation_type === id;
                          return (
                            <Button
                              key={id}
                              type="button"
                              size="sm"
                              variant={active ? "default" : "outline"}
                              className="rounded-sm h-7 text-xs"
                              onClick={() => handleClassify(id)}
                              data-testid={`materialidad-reclassify-${id}`}
                            >
                              {label}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              <div className="px-3 py-2" data-testid="materialidad-provider-detail-tabs">
                <Tabs value={detailTab} onValueChange={setDetailTab} className="space-y-2">
                  <TabsList className="rounded-sm h-auto flex flex-wrap gap-1">
                    <TabsTrigger
                      value="cfdi_pagos"
                      className="rounded-sm text-xs"
                      data-testid="materialidad-detail-tab-cfdi-pagos"
                    >
                      CFDI y complementos de pago
                    </TabsTrigger>
                    <TabsTrigger
                      value="documentos"
                      className="rounded-sm text-xs"
                      data-testid="materialidad-detail-tab-docs"
                    >
                      Documentos
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="cfdi_pagos" className="mt-0 space-y-3 px-1 pb-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-xs text-slate-600 m-0 flex-1">
                        Facturas (I/E) y complementos de pago (P) del Visor · en bloques separados por mes
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="rounded-sm h-7 text-xs"
                        disabled={syncingOps || openingOp}
                        onClick={handleSyncOperations}
                        data-testid="materialidad-operations-sync"
                      >
                        {syncingOps ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                        Crear operaciones MAT-*
                      </Button>
                    </div>

                    {showPriorYearBlock && Number(expediente?.prior_year_payments_mxn || 0) > 0.009 ? (
                      <div
                        className="rounded-sm border border-amber-200 bg-amber-50/70 px-3 py-2 space-y-1"
                        data-testid="materialidad-prior-year-payments"
                      >
                        <p className="m-0 text-[11px] font-semibold text-amber-950 uppercase tracking-wide">
                          Pagos a facturas de periodos anteriores
                        </p>
                        <p className="m-0 text-xs text-amber-950">
                          Total en el periodo:{" "}
                          <span className="font-semibold">{money(expediente.prior_year_payments_mxn)}</span>
                        </p>
                        <p className="m-0 text-[10px] text-amber-900 leading-snug">
                          Solo complementos de pago (P) de este periodo ligados a facturas de años previos
                          (ImpPagado). No suma facturas I ni cobros del mismo año. Informativo: no entra al
                          total facturado (I − E) ni al semáforo Alto.
                        </p>
                      </div>
                    ) : null}

                    {riskFilter === COUNTERPARTY_RISK_FILTER_ALTO ? (
                      <p
                        className="m-0 text-[10px] text-slate-600 bg-slate-50 border border-slate-100 rounded-sm px-2 py-1.5"
                        data-testid="materialidad-alto-ie-only-hint"
                      >
                        Filtro <strong>Alto</strong>: facturas I y NC del ejercicio en un bloque; complementos P
                        (con leyenda de años anteriores) en otro. Se ocultan NC a facturas de años previos.
                      </p>
                    ) : null}

                    {cfdiByMonthFacturas.length === 0 && cfdiByMonthPagos.length === 0 ? (
                      <p className="text-xs text-slate-500 px-1 py-2">
                        Sin CFDI en el periodo. Pulse «Volver a detectar desde CFDI» o cambie el ejercicio.
                      </p>
                    ) : (
                      <div className="space-y-4" data-testid="materialidad-cfdi-by-month">
                        <div className="space-y-2" data-testid="materialidad-cfdi-facturas-by-month">
                          <p className="text-[11px] font-semibold text-slate-500 m-0 uppercase tracking-wide">
                            Facturas y egresos (I − E)
                          </p>
                          <p className="text-[10px] text-slate-500 m-0">
                            Solo facturas I y notas de crédito E. Total del mes = I − E (vigentes). No incluye
                            complementos de pago.
                          </p>
                          {cfdiByMonthFacturas.length === 0 ? (
                            <p className="text-xs text-slate-500 px-1 py-1 m-0">Sin facturas/egresos en el periodo.</p>
                          ) : (
                            cfdiByMonthFacturas.map((bucket) => {
                              const monthKey = `f:${bucket.periodKey}`;
                              const open = openMonths[monthKey] === true;
                              return (
                                <div
                                  key={monthKey}
                                  className="rounded-sm border border-slate-200 overflow-hidden"
                                  data-testid={`materialidad-cfdi-month-facturas-${bucket.periodKey}`}
                                >
                                  <button
                                    type="button"
                                    className="w-full flex flex-wrap items-center gap-2 px-3 py-2 bg-slate-50 text-left text-xs"
                                    aria-expanded={open}
                                    onClick={() => setOpenMonths((prev) => ({
                                      ...prev,
                                      [monthKey]: !prev[monthKey],
                                    }))}
                                    data-testid={`materialidad-cfdi-month-trigger-facturas-${bucket.periodKey}`}
                                  >
                                    <ChevronDown
                                      className={[
                                        "h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform",
                                        open ? "rotate-0" : "-rotate-90",
                                      ].join(" ")}
                                      aria-hidden
                                    />
                                    <span className="font-semibold text-[#0F2D5C]">{bucket.label}</span>
                                    <span className="text-slate-500">{bucket.rows.length} CFDI</span>
                                    <span className="text-slate-500">I:{bucket.countI} E:{bucket.countE}</span>
                                    <span className="ml-auto font-semibold text-slate-700">{money(bucket.total)}</span>
                                  </button>
                                  {open ? (
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="bg-white text-left text-[10px] uppercase text-slate-500 border-t border-slate-100">
                                            <th className="px-2 py-1.5">Fecha</th>
                                            <th className="px-2 py-1.5">UUID</th>
                                            <th className="px-2 py-1.5">Operación</th>
                                            <th className="px-2 py-1.5">Tipo</th>
                                            <th className="px-2 py-1.5">Pago</th>
                                            <th className="px-2 py-1.5 text-right">Total</th>
                                            <th className="px-2 py-1.5" />
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {bucket.rows.map((c) => {
                                            const activeOp = operationDetail?.operation?.cfdi_uuid === c.uuid;
                                            return (
                                              <tr
                                                key={c.uuid}
                                                className={[
                                                  "border-t border-slate-100",
                                                  activeOp ? "bg-[#0F2D5C]/[0.04]" : "",
                                                ].join(" ")}
                                              >
                                                <td className="px-2 py-1.5 whitespace-nowrap">{String(c.fecha || "").slice(0, 10)}</td>
                                                <td className="px-2 py-1.5 font-mono text-[10px]">{String(c.uuid || "").slice(0, 13)}…</td>
                                                <td className="px-2 py-1.5 font-mono text-[10px]">
                                                  {c.case_code || "—"}
                                                  {c.operation_status ? (
                                                    <span className="block text-slate-500 font-sans">
                                                      {OP_STATUS_LABELS[c.operation_status] || c.operation_status}
                                                    </span>
                                                  ) : null}
                                                </td>
                                                <td className="px-2 py-1.5">{tipoComprobanteLabel(c.tipo_comprobante)}</td>
                                                <td className="px-2 py-1.5">{c.metodo_pago || "—"}</td>
                                                <td className="px-2 py-1.5 text-right">{money(c.total)}</td>
                                                <td className="px-2 py-1.5 text-right">
                                                  <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-6 px-2 text-[10px] text-[#0F2D5C]"
                                                    disabled={openingOp}
                                                    onClick={() => handleOpenOperation(c.uuid)}
                                                    data-testid={`materialidad-op-open-${c.uuid}`}
                                                  >
                                                    Abrir
                                                  </Button>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })
                          )}
                        </div>

                        <div className="space-y-2" data-testid="materialidad-cfdi-pagos-by-month">
                          <p className="text-[11px] font-semibold text-slate-500 m-0 uppercase tracking-wide">
                            Complementos de pago (P)
                          </p>
                          <p className="text-[10px] text-slate-500 m-0">
                            Solo complementos P. No se mezclan con facturas. Los de años anteriores llevan nota
                            y suman al apartado superior (no entran al total facturado I − E).
                          </p>
                          {cfdiByMonthPagos.length === 0 ? (
                            <p className="text-xs text-slate-500 px-1 py-1 m-0">Sin complementos de pago en el periodo.</p>
                          ) : (
                            cfdiByMonthPagos.map((bucket) => {
                              const monthKey = `p:${bucket.periodKey}`;
                              const open = openMonths[monthKey] === true;
                              return (
                                <div
                                  key={monthKey}
                                  className="rounded-sm border border-slate-200 overflow-hidden"
                                  data-testid={`materialidad-cfdi-month-pagos-${bucket.periodKey}`}
                                >
                                  <button
                                    type="button"
                                    className="w-full flex flex-wrap items-center gap-2 px-3 py-2 bg-slate-50 text-left text-xs"
                                    aria-expanded={open}
                                    onClick={() => setOpenMonths((prev) => ({
                                      ...prev,
                                      [monthKey]: !prev[monthKey],
                                    }))}
                                    data-testid={`materialidad-cfdi-month-trigger-pagos-${bucket.periodKey}`}
                                  >
                                    <ChevronDown
                                      className={[
                                        "h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform",
                                        open ? "rotate-0" : "-rotate-90",
                                      ].join(" ")}
                                      aria-hidden
                                    />
                                    <span className="font-semibold text-[#0F2D5C]">{bucket.label}</span>
                                    <span className="text-slate-500">{bucket.countP} complemento(s)</span>
                                    <span className="ml-auto font-semibold text-slate-700">
                                      {money(bucket.rows.reduce((acc, r) => acc + Math.abs(Number(r.total) || 0), 0))}
                                    </span>
                                    {bucket.priorYearPayments > 0.009 ? (
                                      <span className="w-full text-[10px] text-amber-800 font-normal">
                                        Pagos a facturas de años anteriores: {money(bucket.priorYearPayments)} (con nota en filas)
                                      </span>
                                    ) : null}
                                  </button>
                                  {open ? (
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="bg-white text-left text-[10px] uppercase text-slate-500 border-t border-slate-100">
                                            <th className="px-2 py-1.5">Fecha</th>
                                            <th className="px-2 py-1.5">UUID</th>
                                            <th className="px-2 py-1.5">Operación</th>
                                            <th className="px-2 py-1.5">Tipo</th>
                                            <th className="px-2 py-1.5">Nota</th>
                                            <th className="px-2 py-1.5 text-right">Total</th>
                                            <th className="px-2 py-1.5" />
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {bucket.rows.map((c) => {
                                            const activeOp = operationDetail?.operation?.cfdi_uuid === c.uuid;
                                            return (
                                              <tr
                                                key={c.uuid}
                                                className={[
                                                  "border-t border-slate-100",
                                                  activeOp ? "bg-[#0F2D5C]/[0.04]" : "",
                                                ].join(" ")}
                                              >
                                                <td className="px-2 py-1.5 whitespace-nowrap">{String(c.fecha || "").slice(0, 10)}</td>
                                                <td className="px-2 py-1.5 font-mono text-[10px]">{String(c.uuid || "").slice(0, 13)}…</td>
                                                <td className="px-2 py-1.5 font-mono text-[10px]">
                                                  {c.case_code || "—"}
                                                  {c.operation_status ? (
                                                    <span className="block text-slate-500 font-sans">
                                                      {OP_STATUS_LABELS[c.operation_status] || c.operation_status}
                                                    </span>
                                                  ) : null}
                                                </td>
                                                <td className="px-2 py-1.5">{tipoComprobanteLabel(c.tipo_comprobante)}</td>
                                                <td className="px-2 py-1.5 text-[10px] text-amber-900 max-w-[220px]">
                                                  {c.note
                                                    || (isPriorYearPayment(c)
                                                      ? "Pago a factura(s) de año(s) anterior(es)"
                                                      : "—")}
                                                </td>
                                                <td className="px-2 py-1.5 text-right">{money(c.total)}</td>
                                                <td className="px-2 py-1.5 text-right">
                                                  <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-6 px-2 text-[10px] text-[#0F2D5C]"
                                                    disabled={openingOp}
                                                    onClick={() => handleOpenOperation(c.uuid)}
                                                    data-testid={`materialidad-op-open-${c.uuid}`}
                                                  >
                                                    Abrir
                                                  </Button>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                    <p className="text-[10px] text-slate-500 m-0">
                      {expediente?.disclaimer}
                      {" "}Pulse «Abrir» para el expediente MAT-* de la operación.
                    </p>
                  </TabsContent>

                  <TabsContent value="documentos" className="mt-0 space-y-2 px-1 pb-2">
                    <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 m-0">
                      Carga de documentos de materialidad
                    </h4>
                    <p className="text-[11px] text-slate-500 m-0">
                      Aquí cargan el despacho o el cliente desde su oficina (Portal de materialidad / enlace seguro de solicitud).
                      Los archivos se versionan; no se sobrescriben.
                    </p>
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-600 min-w-[160px]">
                        Tipo
                        <select
                          className="h-8 rounded-sm border border-slate-200 bg-white px-2 text-sm font-normal"
                          value={docType}
                          onChange={(e) => setDocType(e.target.value)}
                        >
                          {docTypes.map((t) => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                      </label>
                      <label className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-slate-200 bg-white text-xs font-medium text-[#0F2D5C] cursor-pointer hover:bg-slate-50">
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                          disabled={uploading}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleUpload(f);
                            e.target.value = "";
                          }}
                          data-testid="materialidad-cp-upload-input"
                        />
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                        Subir desde el despacho
                      </label>
                    </div>
                    <ul className="rounded-sm border border-slate-200 divide-y divide-slate-100">
                      {(expediente?.documents || []).length === 0 ? (
                        <li className="px-3 py-3 text-xs text-slate-500">
                          Sin documentos aún. Suba desde aquí o pida al cliente cargar desde el Portal.
                        </li>
                      ) : (
                        (expediente?.documents || []).map((d) => (
                          <li key={d.id} className="px-3 py-2 text-xs flex flex-wrap justify-between gap-2">
                            <span>
                              <span className="font-semibold text-slate-800">{d.document_type_label || d.document_type}</span>
                              <span className="text-slate-500"> · v{d.version} · {d.filename}</span>
                            </span>
                            <a
                              className="text-[#0F2D5C] underline shrink-0"
                              href={`/api/tramites/materialidad/files/${encodeURIComponent(d.file_id)}`}
                              download
                            >
                              Descargar
                            </a>
                          </li>
                        ))
                      )}
                    </ul>
                  </TabsContent>
                </Tabs>
              </div>

              {operationDetail?.operation ? (
                <div
                  className="px-4 py-3 space-y-3 bg-slate-50/70 border-t border-slate-200"
                  data-testid="materialidad-operation-detail"
                >
                  <div className="flex flex-wrap items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-bold text-[#0F2D5C] m-0">
                        {operationDetail.operation.case_code}
                      </h4>
                      <p className="text-[11px] font-mono text-slate-600 m-0 mt-0.5">
                        {operationDetail.operation.cfdi_uuid}
                      </p>
                      <p className="text-[11px] text-slate-500 m-0 mt-1">
                        {OP_STATUS_LABELS[operationDetail.operation.status] || operationDetail.operation.status}
                        {" · "}
                        {money(operationDetail.operation.total)}
                        {" · "}
                        {String(operationDetail.operation.fecha || "").slice(0, 10)}
                      </p>
                    </div>
                    <span
                      className={`inline-flex rounded-sm border px-2 py-1 text-[10px] font-semibold ${
                        INTEGRATION_CLASS[operationDetail.integration_level] || INTEGRATION_CLASS.incompleto
                      }`}
                    >
                      {operationDetail.integration_label || "—"}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={() => setOperationDetail(null)}
                      aria-label="Cerrar operación"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-7 text-xs"
                      onClick={() => handlePatchOperation({ status: "en_revision" })}
                    >
                      En revisión
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-sm h-7 text-xs"
                      onClick={() => handlePatchOperation({ status: "completo" })}
                    >
                      Marcar completa
                    </Button>
                    <Button asChild type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs">
                      <a
                        href={materialidadOperationFolderZipUrl(operationDetail.operation.id)}
                        download
                        data-testid="materialidad-op-download-zip"
                      >
                        <Download className="h-3.5 w-3.5 mr-1" />
                        ZIP operación
                      </a>
                    </Button>
                    <Button asChild type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs">
                      <a href={materialidadOperationFolderResumenPdfUrl(operationDetail.operation.id)} download>
                        PDF
                      </a>
                    </Button>
                    <Button asChild type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs">
                      <a href={materialidadOperationFolderCartaPdfUrl(operationDetail.operation.id)} download>
                        Carta
                      </a>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="rounded-sm h-7 text-xs"
                      disabled={emailBusy}
                      onClick={() => handlePrepareEmail("operation")}
                    >
                      <Mail className="h-3.5 w-3.5 mr-1" />
                      Correo
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="rounded-sm h-7 text-xs"
                      disabled={emailBusy}
                      onClick={() => handleSendEmail("operation")}
                    >
                      Enviar ZIP
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <h5 className="text-[11px] font-bold uppercase tracking-wide text-slate-500 m-0">
                      Documentos de la operación
                    </h5>
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-600 min-w-[180px]">
                        Tipo
                        <select
                          className="h-8 rounded-sm border border-slate-200 bg-white px-2 text-sm font-normal"
                          value={opDocType}
                          onChange={(e) => setOpDocType(e.target.value)}
                        >
                          {(operationDetail.document_types || []).map((t) => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                      </label>
                      <label className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-slate-200 bg-white text-xs font-medium text-[#0F2D5C] cursor-pointer hover:bg-slate-50">
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                          disabled={uploadingOp}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleUploadOperationDoc(f);
                            e.target.value = "";
                          }}
                          data-testid="materialidad-op-upload-input"
                        />
                        {uploadingOp ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
                        Subir
                      </label>
                    </div>
                    <ul className="rounded-sm border border-slate-200 bg-white divide-y divide-slate-100">
                      {(operationDetail.documents || []).length === 0 ? (
                        <li className="px-3 py-3 text-xs text-slate-500">Sin documentos de operación.</li>
                      ) : (
                        (operationDetail.documents || []).map((d) => (
                          <li key={d.id} className="px-3 py-2 text-xs flex flex-wrap justify-between gap-2">
                            <span>
                              <span className="font-semibold text-slate-800">{d.document_type_label || d.document_type}</span>
                              <span className="text-slate-500"> · v{d.version} · {d.filename}</span>
                            </span>
                            <a
                              className="text-[#0F2D5C] underline shrink-0"
                              href={`/api/tramites/materialidad/files/${encodeURIComponent(d.file_id)}`}
                              download
                            >
                              Descargar
                            </a>
                          </li>
                        ))
                      )}
                    </ul>
                    <p className="text-[10px] text-slate-500 m-0">{operationDetail.disclaimer}</p>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
